import { Tag } from "components/DesignSystem/Tag/Tag";
import { CheckCircleFilled } from "components/icons/CheckCircleFilled";
import {
  CLICKED_UPLOAD_AREA_ON_RECORDS_AND_SETUP,
  DROPPED_FILES_ON_RECORDS_AND_SETUP,
} from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { RequiredItem } from "v2/store/entityDocumentsApi";
import { useDocumentUploadFlow } from "v2/utils/useDocumentUploadFlow";
import { Dropzone } from "./Dropzone";
import { RequiredDocumentList } from "./RequiredDocumentList";

type RequiredFromYouProps = {
  entityId: string;
  items: RequiredItem[];
  onUploadFiles: (files: File[]) => Promise<string[]>;

  variant?: "card" | "modal";
};

const AllSet = () => (
  <div className="t-flex t-h-full t-flex-col t-items-center t-justify-center t-gap-1 t-px-5 t-text-center">
    <span className="t-mb-1.5 t-text-dark_green">
      <CheckCircleFilled className="t-h-11 t-w-11" />
    </span>
    <div className="t-text-subtitle-lg t-text-text-60">You are all done!</div>
    <p className="t-max-w-[380px] t-text-body t-text-neutral-50">
      We don't need anything else from you right now. If there are extra
      documents you'd like us to have for context, feel free to drop them in the
      box.
    </p>
  </div>
);

export const RequiredFromYou = ({
  entityId,
  items,
  onUploadFiles,
  variant = "card",
}: RequiredFromYouProps) => {
  const isModal = variant === "modal";
  const { trackEvent } = useAnalytics();

  const {
    status,
    matchedCount,
    isUploading,
    matchedIds,
    visibleItems,
    handleFiles,
  } = useDocumentUploadFlow({ entityId, items, onUploadFiles });

  const hasItems = visibleItems.length > 0;

  const handleUploadAreaClick = (): void =>
    trackEvent(CLICKED_UPLOAD_AREA_ON_RECORDS_AND_SETUP, {
      parse_status: status,
    });

  const handleFilesDropped = (files: File[]): void =>
    trackEvent(DROPPED_FILES_ON_RECORDS_AND_SETUP, {
      parse_status: status,
      file_count: files.length,
    });

  let cardStateClass = "t-border-neutral-10 t-bg-surface";
  if (hasItems) cardStateClass = "pending-card--priority";

  const containerClassName = isModal ? "" : "pending-card-container";

  const cardClassName = isModal
    ? "t-flex t-h-[250px] t-flex-col t-rounded-lg t-border t-border-solid t-border-neutral-0 t-bg-surface t-p-3"
    : `pending-card t-flex t-flex-col t-rounded-2xl t-border t-border-solid t-px-8 t-pt-8 sm:t-px-4 sm:t-pt-4 ${cardStateClass}`;

  const gridClassName = isModal ? "t-flex t-h-full t-gap-3" : "pending-grid";

  const listColClassName = isModal
    ? "t-relative t-min-w-0 t-flex-1"
    : "pending-col-list t-relative t-mb-3";

  const dropColClassName = isModal
    ? "t-h-full t-min-w-0 t-flex-1"
    : "pending-col-drop t-mb-8";

  return (
    <div className={containerClassName}>
      <div className={cardClassName}>
        {!isModal && hasItems && (
          <>
            <div className="t-flex t-items-center t-gap-2.5">
              <h2 className="t-m-0 t-text-subtitle-lg t-text-text-60">
                Pending on you
              </h2>
              <Tag tagType="red" rounded icon size="small">
                Priority
              </Tag>
            </div>
            <p className="t-mb-4 t-mt-1.5 t-max-w-[560px] t-text-body t-text-neutral-50">
              Drop all your files on the right and we'll sort them
              automatically.
            </p>
          </>
        )}

        <div className={gridClassName}>
          <div className={listColClassName}>
            {!hasItems && <AllSet />}

            {hasItems && (
              <RequiredDocumentList
                entityId={entityId}
                items={visibleItems}
                matchedIds={matchedIds}
                isBusy={isUploading}
              />
            )}
          </div>

          <div className={dropColClassName}>
            <Dropzone
              status={status}
              matchedCount={matchedCount}
              hasPendingItems={hasItems}
              onFiles={handleFiles}
              onAreaClick={handleUploadAreaClick}
              onFilesDropped={handleFilesDropped}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
